import { AxiosError } from 'axios';

const RETRYABLE_STATUS_CODES = new Set([502, 503, 504]);

const RETRYABLE_ERROR_CODES = new Set([
  'ECONNRESET',
  'ECONNREFUSED',
  'ENOTFOUND',
  'ETIMEDOUT',
  'ERR_NETWORK',
  'ECONNABORTED',
]);

export class RetryableHttpError extends Error {
  response: { status: number };
  constructor(message: string, statusCode: number) {
    super(message);
    this.name = 'RetryableHttpError';
    this.response = { status: statusCode };
  }
}

export function isRetryableError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;

  const axiosError = error as AxiosError;

  if (axiosError.response) {
    return RETRYABLE_STATUS_CODES.has(axiosError.response.status);
  }

  if (axiosError.code && RETRYABLE_ERROR_CODES.has(axiosError.code)) {
    return true;
  }

  // "socket hang up" doesn't always set a code — check the message
  if (axiosError.message?.toLowerCase().includes('socket hang up')) {
    return true;
  }

  return false;
}

export interface EmbeddedGatewayError {
  statusCode: number;
  error: string;
  message: string;
}

export function isEmbeddedGatewayError(
  data: unknown
): data is EmbeddedGatewayError {
  if (typeof data !== 'object' || data === null) return false;
  const d = data as Record<string, unknown>;
  return (
    typeof d.statusCode === 'number' &&
    d.statusCode >= 502 &&
    typeof d.error === 'string' &&
    typeof d.message === 'string'
  );
}

export interface RetryOptions {
  signal?: AbortSignal;
  onRetry?: (attempt: number, error: unknown) => void;
}

// fn receives the AbortSignal so it can cancel the in-flight request if the
// caller aborts between retries (e.g. on component unmount).
export async function retryWithBackoff<T>(
  fn: (signal?: AbortSignal) => Promise<T>,
  maxRetries = 3,
  getEmbeddedError?: (result: T) => Error | null,
  options?: RetryOptions
): Promise<T> {
  let lastError: unknown;
  const { signal, onRetry } = options ?? {};

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    if (signal?.aborted) throw signal.reason;

    if (attempt > 0) {
      const delayMs = Math.pow(2, attempt - 1) * 1000 + Math.random() * 1000;
      await new Promise(resolve => setTimeout(resolve, delayMs));
      if (signal?.aborted) throw signal.reason;
    }

    try {
      const result = await fn(signal);

      const embeddedError = getEmbeddedError?.(result) ?? null;
      if (embeddedError) {
        lastError = embeddedError;
        if (attempt < maxRetries) {
          onRetry?.(attempt + 1, embeddedError);
          continue;
        }
        throw embeddedError;
      }

      return result;
    } catch (error) {
      lastError = error;

      if (!isRetryableError(error) || attempt === maxRetries) {
        throw error;
      }

      onRetry?.(attempt + 1, error);
    }
  }

  throw lastError;
}
